/**
 * The circles, always on screen — and under them, what is coming up.
 *
 * Circles are the idea the product is built on, and until now they lived
 * behind a button. This is the same tree the Circles panel draws, in its
 * compact form — swatch, name, count, eye — docked under the calendar's name
 * so that hiding a circle or seeing which ones there are is one click, not
 * two. Anything more than a toggle opens the full panel on that circle.
 *
 * A legend that only names groups answers half the question. The other half is
 * "who is next?", which the ring answers by being read clockwise from the today
 * hand, so the dock reads it out: the next entries from today forward, each a
 * face, a name and the day. Pressing one selects it on the ring, exactly as
 * clicking its card would.
 *
 * Pressing a circle's **name** shows that circle alone — the list narrows to
 * its subtree and the ring lights that circle's own ring — which is the press a
 * viewer wants as much as an owner, and is why opening the panel moved to the
 * chevron beside it.
 *
 * It folds. The ring is the page, and on a phone the dock would otherwise sit
 * over the top of it, so it opens on a wide screen and starts folded on a
 * narrow one; whichever the person chooses is remembered on this device.
 */
import { descendantIds, type CalEvent } from '@circle/shared';
import { safeStorage } from '../core/api';
import { svgEl } from '../core/svg';
import { avatarDisc } from '../fragments/default/avatar';
import { CirclesView, type CirclesPanelOptions } from './circles-panel';
import { button, countBadge, el, setChildren, type Child } from './dom';
import type { PendingQueue } from './pending';
import { isNarrow } from './topbar';
import { upcoming, upcomingWhen, type UpcomingRow } from './upcoming';

const FOLD_KEY = 'circle:dock-folded';

/** How many rows the list shows, and how many more each "Show more" reveals. */
export const UPCOMING_PAGE = 8;

/** The face on a row, in screen px. Big enough to recognise, small enough to list. */
const FACE_RADIUS = 12;

export interface CirclesDockOptions
  extends Omit<CirclesPanelOptions, 'panel' | 'compact' | 'pending' | 'onFilter' | 'filteredCircle'> {
  /** Open the full Circles panel, on this circle if one is named. */
  onOpen: (circleId?: string) => void;
  /** The owner's moderation queue: its count sits on the fold, folded or not. */
  pending?: PendingQueue;
  /** The year the ring is showing; the list is dated from inside it. */
  year: number;
  today?: Date;
  /** A row was pressed: select that entry on the ring and bring it into view. */
  onSelectEvent: (eventId: string) => void;
  /** Light one circle's ring and let everything else recede; null clears it. */
  onHighlight?: (tagId: string | null) => void;
}

export class CirclesDock {
  readonly root: HTMLElement;
  private readonly options: CirclesDockOptions;
  private readonly view: CirclesView;
  private readonly body: HTMLElement;
  private readonly fold: HTMLButtonElement;
  private readonly list: HTMLElement;
  private readonly pending: PendingQueue | undefined;
  private readonly unwatch: (() => void) | undefined;
  private readonly today: Date;
  private folded: boolean;
  private year: number;
  /** The circle the names have been pressed down to, or null for all of them. */
  private circleId: string | null = null;
  /** What the search field says, as a question about one entry. */
  private search: ((event: CalEvent) => boolean) | null = null;
  private shown = UPCOMING_PAGE;

  constructor(options: CirclesDockOptions) {
    // A viewer has no panel to edit in, so only an owner gets the chevron.
    // The dock draws no sources section, so the queue is kept for its count.
    const { onOpen, pending, year, today, onSelectEvent, onHighlight, ...rest } = options;
    this.options = options;
    this.pending = pending;
    this.year = year;
    this.today = today ?? new Date();
    this.view = new CirclesView({
      ...rest,
      panel: undefined as never,
      compact: true,
      onFilter: (circleId) => this.toggleCircle(circleId),
      filteredCircle: () => this.circleId,
      ...(options.editable ? { onOpen } : {}),
    });
    this.unwatch = pending?.watch(() => this.paintFold());
    this.folded = readFold() ?? isNarrow();

    this.fold = el('button', {
      class: 'circles-dock__fold',
      type: 'button',
      'aria-expanded': this.folded ? 'false' : 'true',
      'aria-controls': 'circles-dock-body',
      onClick: () => this.setFolded(!this.folded),
    });
    this.list = el('section', { class: 'dock-up', 'aria-label': 'Coming up' });
    this.body = el(
      'div',
      { class: 'circles-dock__body', id: 'circles-dock-body' },
      this.view.root,
      this.list,
    );
    if (options.editable) {
      this.body.appendChild(
        el(
          'div',
          { class: 'circles-dock__foot' },
          button('Edit circles', () => options.onOpen(), { class: 'btn--small btn--quiet' }),
        ),
      );
    }
    this.root = el(
      'aside',
      { class: 'circles-dock', 'aria-label': 'Circles' },
      el('div', { class: 'circles-dock__head' }, this.fold),
      this.body,
    );
    this.paintFold();
    this.paintUpcoming();
  }

  /** The bundle changed underneath: redraw from the page's arrays. */
  paint(): void {
    this.view.paint();
    this.paintUpcoming();
  }

  /** The ring is showing another year, so another twelve months are coming up. */
  setYear(year: number): void {
    if (year === this.year) return;
    this.year = year;
    this.shown = UPCOMING_PAGE;
    this.paintUpcoming();
  }

  /**
   * The search, as the ring has it. The list shows what matches **both** this
   * and the circle the names are pressed down to; the ring applies the same
   * two, by its own two mechanisms (ui/ring-view.ts `setFilter`).
   */
  setSearch(keep: ((event: CalEvent) => boolean) | null): void {
    this.search = keep;
    this.shown = UPCOMING_PAGE;
    this.paintUpcoming();
  }

  /** Which circle the dock is pressed down to, if any. */
  get circleFilter(): string | null {
    return this.circleId;
  }

  destroy(): void {
    this.unwatch?.();
    this.view.destroy();
  }

  private toggleCircle(circleId: string): void {
    // The tree is rebuilt from the new state, so the button just pressed is
    // about to stop existing. A keyboard would be left on nothing; this puts
    // it on the row's new button, which now says the opposite.
    const active = this.root.ownerDocument.activeElement;
    const refocus =
      active instanceof HTMLElement &&
      (active.dataset.role === 'filter' || active.dataset.role === 'clear-circle');

    this.circleId = this.circleId === circleId ? null : circleId;
    this.shown = UPCOMING_PAGE;
    // The ring dims by circle and the search dims by match; they are different
    // mechanisms on purpose, and `RingView.setFilter` says why they compose.
    this.options.onHighlight?.(this.circleId);
    this.view.paint();
    this.paintUpcoming();
    if (refocus) this.focusName(circleId);
  }

  private focusName(circleId: string): void {
    for (const node of this.view.root.querySelectorAll<HTMLElement>('[data-role="filter"]')) {
      if (node.closest('.circle')?.getAttribute('data-circle') === circleId) {
        node.focus();
        return;
      }
    }
  }

  /** The two filters as one question about an entry, or nothing to ask. */
  private keep(): ((event: CalEvent) => boolean) | undefined {
    const search = this.search;
    const circle = this.subtree();
    if (!search && !circle) return undefined;
    return (event) =>
      (!search || search(event)) &&
      (!circle || (event.tagIds ?? []).some((id) => circle.has(id)));
  }

  /**
   * The pressed circle and everything inside it. Family includes Kids: a
   * circle stands for the people under it as much as the ones in it, which is
   * the arithmetic `@circle/shared`'s `tree.ts` does for the panel and the
   * worker too.
   */
  private subtree(): Set<string> | null {
    if (!this.circleId) return null;
    const ids = descendantIds(this.options.tags(), this.circleId);
    ids.add(this.circleId);
    return ids;
  }

  private paintUpcoming(): void {
    const keep = this.keep();
    const rows = upcoming(this.options.events(), {
      year: this.year,
      today: this.today,
      ...(keep ? { keep } : {}),
    });
    const shown = rows.slice(0, this.shown);
    const name = this.circleId
      ? (this.options.tags().find((t) => t.id === this.circleId)?.name ?? null)
      : null;

    const body: Child[] = [
      el(
        'div',
        { class: 'dock-up__head' },
        el('h3', { class: 'dock-up__title', text: 'Coming up' }),
        name
          ? el('button', {
              class: 'dock-up__all',
              type: 'button',
              'data-role': 'clear-circle',
              'aria-label': `Showing ${name} only; show every circle`,
              title: 'Show every circle',
              text: '× All',
              onClick: () => this.toggleCircle(this.circleId ?? ''),
            })
          : null,
      ),
    ];
    if (shown.length === 0) {
      body.push(
        el('p', {
          class: 'dock-up__empty small muted',
          text: keep ? 'Nothing here matches.' : 'Nothing on the ring yet.',
        }),
      );
    } else {
      body.push(
        el('ul', { class: 'dock-up__list' }, ...shown.map((row) => this.row(row))),
      );
    }
    if (rows.length > shown.length) {
      body.push(
        button(
          'Show more',
          () => {
            this.shown += UPCOMING_PAGE;
            this.paintUpcoming();
          },
          { class: 'btn--small btn--quiet dock-up__more', 'data-role': 'show-more' },
        ),
      );
    }
    setChildren(this.list, body);
  }

  private row(row: UpcomingRow): HTMLElement {
    const { event } = row;
    return el(
      'li',
      { class: 'dock-up__item' },
      el(
        'button',
        {
          class: 'dock-up__row',
          type: 'button',
          'data-event': event.id,
          onClick: () => this.options.onSelectEvent(event.id),
        },
        this.face(event),
        el('span', { class: 'dock-up__name', text: event.title }),
        el('span', { class: 'dock-up__when', text: upcomingWhen(row) }),
      ),
    );
  }

  /**
   * The same disc the card on the ring draws, at a twelfth of the area: the
   * photo when there is one, the initials underneath it when there is not.
   */
  private face(event: CalEvent): SVGSVGElement {
    const size = FACE_RADIUS * 2 + 2;
    const svg = svgEl('svg', {
      class: 'dock-up__face',
      width: size,
      height: size,
      viewBox: `0 0 ${size} ${size}`,
      'aria-hidden': 'true',
      focusable: 'false',
    });
    svg.append(
      avatarDisc({
        cx: size / 2,
        cy: size / 2,
        r: FACE_RADIUS,
        event,
        colour: this.colourOf(event),
        ring: 1,
      }),
    );
    return svg;
  }

  /**
   * A face's colour without the cascade: the first circle that has one, and
   * the source's otherwise. The full resolution is the scene's and needs the
   * theme, the tag path and the event's own tokens; a 24px disc in a list does
   * not earn that, and these are the two levels anyone actually sets.
   */
  private colourOf(event: CalEvent): string {
    const tags = this.options.tags();
    for (const id of event.tagIds ?? []) {
      const colour = tags.find((t) => t.id === id)?.colour;
      if (colour) return colour;
    }
    return this.options.sources().find((s) => s.id === event.sourceId)?.colour ?? 'var(--ui-dim)';
  }

  private setFolded(folded: boolean): void {
    this.folded = folded;
    writeFold(folded);
    this.paintFold();
  }

  private paintFold(): void {
    this.root.dataset.folded = this.folded ? 'true' : 'false';
    this.fold.setAttribute('aria-expanded', this.folded ? 'false' : 'true');
    // Named for what it does, so it is not a second "Circles" button beside
    // the top bar's — to a screen reader, or to anything looking one up by name.
    this.fold.setAttribute('aria-label', this.folded ? 'Show the circles' : 'Hide the circles');
    const waiting = this.pending?.count ?? 0;
    setChildren(this.fold, [
      el('span', { class: 'circles-dock__title', text: 'Circles' }),
      countBadge(waiting, `${waiting} waiting for you`),
      el('span', { class: 'circles-dock__chevron', 'aria-hidden': 'true', text: this.folded ? '▸' : '▾' }),
    ]);
    this.body.hidden = this.folded;
  }
}

function readFold(): boolean | null {
  try {
    const raw = safeStorage()?.getItem(FOLD_KEY);
    return raw === null || raw === undefined ? null : raw === 'true';
  } catch {
    return null;
  }
}

function writeFold(folded: boolean): void {
  try {
    safeStorage()?.setItem(FOLD_KEY, folded ? 'true' : 'false');
  } catch {
    /* a device that cannot remember asks the width again next time */
  }
}
